import { App, Alert, Button, Descriptions, Modal, Space, Typography } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { fetchGatewaySettings, fetchIsupKey, type AttendanceDevice } from './attendanceApi'

/**
 * KAMERA MENYUSIGA TERILADIGAN to'rtta qiymat.
 *
 * <h2>Bu oyna nima uchun bor</h2>
 * Filialda VPN yo'q. Kamera oldida turgan TEXNIK uning menyusiga to'rt
 * narsani qo'lda teradi: ISUP server manzili, port, Device ID va kalit.
 * Admin bu to'rttasini shu ekrandan o'qib texnikka aytadi (yoki nusxa
 * olib yuboradi).
 *
 * <h2>⚠️ QIYMATLAR OCHIQ KO'RSATILADI — VA BU ATAYLAB</h2>
 * Kalit yulduzcha ortiga yashirilmaydi. U «sizib ketmasligi kerak
 * bo'lgan sir» emas, ATAYLAB uzatiladigan ma'lumot: uzatilmasa kamera
 * umuman ulanmaydi. Yashirish bu ekranning butun maqsadini bekor
 * qilardi va admin qiymatni baribir boshqa yo'l bilan (bazadan,
 * so'rovdan) qidirishga majbur bo'lardi.
 *
 * Kalit shunga qaramay RO'YXAT javobida kelmaydi — u shu oyna
 * ochilganda alohida so'rov bilan olinadi va backend o'sha so'rovni
 * audit qiladi (`fetchIsupKey` izohi).
 *
 * <h2>⚠️ SERVER MANZILI YO'Q BO'LSA — OYNA BARIBIR OCHILADI</h2>
 * Manzil va port o'rnatma sozlamasidan keladi va u so'rov yiqilishi
 * mumkin. O'shanda Device ID bilan kalit BARIBIR ko'rsatiladi: ular
 * qurilma yozuvida bor va texnikka ularning o'zi ham kerak. Manzil
 * o'rniga esa nima bo'lgani ochiq yoziladi — to'ldiruvchi qiymat
 * («localhost», «—») KO'RSATILMAYDI, chunki uni terib ko'rgan texnik
 * sababini hech qayerdan bilmasdi.
 */
export function DeviceConnectionInfoModal({
  device,
  onClose,
}: {
  /** `null` — oyna yopiq */
  device: AttendanceDevice | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()

  const open = device !== null

  /*
    ⚠️ Kalit FAQAT oyna ochilganda so'raladi (`enabled`), ro'yxat bilan
    birga emas. Har qatorda oldindan olib qo'yish sirni kerak
    bo'lmaganda ham tarmoqdan o'tkazardi va audit jurnalini «hech kim
    ko'rmagan» so'rovlar bilan to'ldirardi.
  */
  const key = useQuery({
    queryKey: [...queryKeys.attendance.all, 'isup-key', device?.id],
    queryFn: () => fetchIsupKey(device!.id),
    enabled: open,
    // Sir keshda uzoq turmasin — oyna yopilgach keyingi ochilishda
    // qaytadan so'raladi va audit izi ham to'liq qoladi.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  })

  const settings = useQuery({
    queryKey: [...queryKeys.attendance.all, 'gateway-settings'],
    queryFn: fetchGatewaySettings,
    enabled: open,
    retry: false,
  })

  const host = settings.data?.isupHost ?? null
  const port = settings.data?.isupPort ?? null
  const deviceId = device?.isupDeviceId ?? null
  const isupKey = key.data ?? null

  /*
    ⚠️ «Hammasini nusxa olish» — TO'RTTASI BITTA MATN bo'lib ketadi,
    chunki admin ularni odatda bitta xabarda (Telegram, SMS) texnikka
    yuboradi. To'rt marta alohida nusxa olish o'sha xabarni qo'lda
    yig'ishga majbur qilardi va aynan o'sha yerda qiymat aralashib
    ketardi.

    ⚠️ Yorliqlar TARJIMADAN olinadi: matn admin tilida ketadi.
  */
  const allText = [
    `${t('attendance.isupHost')}: ${host ?? t('attendance.gatewaySettingsMissingShort')}`,
    `${t('attendance.isupPort')}: ${port ?? t('attendance.gatewaySettingsMissingShort')}`,
    `${t('attendance.isupDeviceId')}: ${deviceId ?? ''}`,
    `${t('attendance.isupKey')}: ${isupKey ?? ''}`,
  ].join('\n')

  /*
    ⚠️ `navigator.clipboard` HTTPS'siz kontekstda MAVJUD EMAS va panel
    ichki tarmoqda `http://` orqali ochilishi mumkin
    (`TenantCredentialsModal` da aynan shu holat o'lchangan). Shuning
    uchun mavjudligi tekshiriladi va bo'lmasa OCHIQ aytiladi — jimgina
    hech narsa qilmaslik «tugma ishlamadi» bo'lib o'qilardi.
  */
  const copy = (value: string | null) => {
    if (value === null || value === '') return
    if (!navigator.clipboard) {
      message.warning(t('attendance.copyUnavailable'))
      return
    }
    void navigator.clipboard.writeText(value).then(
      () => message.success(t('attendance.copied')),
      () => message.warning(t('attendance.copyUnavailable')),
    )
  }

  /** Qiymat + «nusxa» tugmasi — to'rt qatorning bittasi */
  const row = (value: string | null, loading: boolean, missing: string) =>
    loading ? (
      <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
    ) : value === null ? (
      <Typography.Text type="secondary">{missing}</Typography.Text>
    ) : (
      <Space size={4}>
        {/* ⚠️ `code` — qiymat aynan shu ko'rinishda teriladi, shrift
            chalkashlikni kamaytiradi */}
        <Typography.Text code copyable={false}>
          {value}
        </Typography.Text>
        <Button
          size="small"
          type="text"
          icon={<CopyOutlined />}
          onClick={() => copy(value)}
          aria-label={t('attendance.copy')}
        />
      </Space>
    )

  return (
    <Modal
      open={open}
      title={t('attendance.connectionInfoTitle', { name: device?.name ?? '' })}
      onCancel={onClose}
      onOk={onClose}
      okText={t('common.close')}
      cancelButtonProps={{ style: { display: 'none' } }}
      destroyOnHidden
      width={620}
    >
      {/* ⚠️ Ko'rsatma BIRINCHI o'rinda: bu oynani ochgan odam nima
          qilishi kerakligini bilishi shart — qiymatlarni kimga va
          qayerga terish kerakligini. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('attendance.connectionInfoNote')}
      />

      {/* ⚠️ Sozlama olinmagani XATO EMAS deb ko'rsatilmaydi ham,
          MUVAFFAQIYAT deb ham: u ogohlantirish. Qolgan ikki qiymat
          joyida va ular baribir kerak. */}
      {settings.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('attendance.gatewaySettingsMissing')}
          description={translateError(settings.error, t)}
        />
      ) : null}

      {key.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('attendance.isupKeyFailed')}
          description={translateError(key.error, t)}
        />
      ) : null}

      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t('attendance.isupHost')}>
          {row(host, settings.isPending, t('attendance.gatewaySettingsMissingShort'))}
        </Descriptions.Item>
        <Descriptions.Item label={t('attendance.isupPort')}>
          {row(port === null ? null : String(port), settings.isPending, t('attendance.gatewaySettingsMissingShort'))}
        </Descriptions.Item>
        <Descriptions.Item label={t('attendance.isupDeviceId')}>
          {row(deviceId, false, t('common.dash'))}
        </Descriptions.Item>
        <Descriptions.Item label={t('attendance.isupKey')}>
          {row(isupKey, key.isPending, t('attendance.isupKeyMissing'))}
        </Descriptions.Item>
      </Descriptions>

      <Button
        icon={<CopyOutlined />}
        style={{ marginTop: 16 }}
        onClick={() => copy(allText)}
        disabled={deviceId === null && isupKey === null}
      >
        {t('attendance.copyAll')}
      </Button>
    </Modal>
  )
}
